import React from 'react'
import useChains from 'state/chain/hooks'
import type {
  EquityFormData,
  TokenInfo,
} from 'submission/lib/equity-form-defaults'

type Props = {
  form: EquityFormData
  setForm: React.Dispatch<React.SetStateAction<EquityFormData>>
  errors: Record<string, string>
}

const TokenFields: React.FC<{
  label: string
  prefix: 'paymentToken' | 'projectToken'
  value: TokenInfo
  setForm: Props['setForm']
  errors: Props['errors']
}> = ({ label, prefix, value, setForm, errors }) => {
  const update = (k: keyof TokenInfo, v: string) =>
    setForm((p) => ({ ...p, [prefix]: { ...p[prefix], [k]: v } }))
  return (
    <fieldset className="border rounded p-3">
      <legend className="text-sm font-medium text-slate-700 px-1">
        {label}
      </legend>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col text-sm col-span-2">
          <span className="text-slate-700 mb-1">Address *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={value.address}
            onChange={(e) => update('address', e.target.value)}
          />
          {errors[`${prefix}.address`] && (
            <span className="text-red-600 text-xs mt-1">
              {errors[`${prefix}.address`]}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Symbol *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={value.symbol}
            onChange={(e) => update('symbol', e.target.value)}
          />
          {errors[`${prefix}.symbol`] && (
            <span className="text-red-600 text-xs mt-1">
              {errors[`${prefix}.symbol`]}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Name *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={value.name}
            onChange={(e) => update('name', e.target.value)}
          />
          {errors[`${prefix}.name`] && (
            <span className="text-red-600 text-xs mt-1">
              {errors[`${prefix}.name`]}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Decimals *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={value.decimals}
            onChange={(e) => update('decimals', e.target.value)}
          />
          {errors[`${prefix}.decimals`] && (
            <span className="text-red-600 text-xs mt-1">
              {errors[`${prefix}.decimals`]}
            </span>
          )}
        </label>
      </div>
    </fieldset>
  )
}

const TermsSection: React.FC<Props> = ({ form, setForm, errors }) => {
  const chains = useChains()
  return (
    <section className="bg-white border border-slate-200 rounded p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Investment terms
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">
            Min raise threshold (USD) *
          </span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.minRaiseThreshold}
            onChange={(e) =>
              setForm((p) => ({ ...p, minRaiseThreshold: e.target.value }))
            }
          />
          {errors.minRaiseThreshold && (
            <span className="text-red-600 text-xs mt-1">
              {errors.minRaiseThreshold}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">
            Min investment per user (USD) *
          </span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.minInvestmentPerUser}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                minInvestmentPerUser: e.target.value,
              }))
            }
          />
          {errors.minInvestmentPerUser && (
            <span className="text-red-600 text-xs mt-1">
              {errors.minInvestmentPerUser}
            </span>
          )}
        </label>

        <label className="flex items-center text-sm md:col-span-2 gap-2">
          <input
            type="checkbox"
            checked={form.useMaxDeposit}
            onChange={(e) =>
              setForm((p) => ({ ...p, useMaxDeposit: e.target.checked }))
            }
          />
          Use max deposit per user
        </label>
        {form.useMaxDeposit && (
          <label className="flex flex-col text-sm md:col-span-2">
            <span className="text-slate-700 mb-1">Max deposit (USD) *</span>
            <input
              type="number"
              min={0}
              className="border rounded px-2 py-1 text-slate-900 bg-white"
              value={form.maxDeposit}
              onChange={(e) =>
                setForm((p) => ({ ...p, maxDeposit: e.target.value }))
              }
            />
            {errors.maxDeposit && (
              <span className="text-red-600 text-xs mt-1">
                {errors.maxDeposit}
              </span>
            )}
          </label>
        )}

        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Deposit chain *</span>
          <select
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.depositChainId}
            onChange={(e) =>
              setForm((p) => ({ ...p, depositChainId: e.target.value }))
            }
          >
            <option value="">Select…</option>
            {chains.map((c) => (
              <option key={c.chainID} value={String(c.chainID)}>
                {(c as any).name ?? (c as any).chainName ?? c.chainID}
              </option>
            ))}
          </select>
          {errors.depositChainId && (
            <span className="text-red-600 text-xs mt-1">
              {errors.depositChainId}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Distribution chain *</span>
          <select
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.distributionChainId}
            onChange={(e) =>
              setForm((p) => ({ ...p, distributionChainId: e.target.value }))
            }
          >
            <option value="">Select…</option>
            {chains.map((c) => (
              <option key={c.chainID} value={String(c.chainID)}>
                {(c as any).name ?? (c as any).chainName ?? c.chainID}
              </option>
            ))}
          </select>
          {errors.distributionChainId && (
            <span className="text-red-600 text-xs mt-1">
              {errors.distributionChainId}
            </span>
          )}
        </label>

        <div className="md:col-span-2">
          <TokenFields
            label="Payment token"
            prefix="paymentToken"
            value={form.paymentToken}
            setForm={setForm}
            errors={errors}
          />
        </div>
        <div className="md:col-span-2">
          <TokenFields
            label="Project token"
            prefix="projectToken"
            value={form.projectToken}
            setForm={setForm}
            errors={errors}
          />
        </div>

        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Max cap (USD) *</span>
          <input
            type="number"
            min={0}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.maxCap}
            onChange={(e) => setForm((p) => ({ ...p, maxCap: e.target.value }))}
          />
          {errors.maxCap && (
            <span className="text-red-600 text-xs mt-1">{errors.maxCap}</span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Token price (USD) *</span>
          <input
            type="number"
            min={0}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.tokenPrice}
            onChange={(e) =>
              setForm((p) => ({ ...p, tokenPrice: e.target.value }))
            }
          />
          {errors.tokenPrice && (
            <span className="text-red-600 text-xs mt-1">
              {errors.tokenPrice}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Cliff (days) *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.cliffDays}
            onChange={(e) =>
              setForm((p) => ({ ...p, cliffDays: e.target.value }))
            }
          />
          {errors.cliffDays && (
            <span className="text-red-600 text-xs mt-1">
              {errors.cliffDays}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Vesting (days) *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.vestingDays}
            onChange={(e) =>
              setForm((p) => ({ ...p, vestingDays: e.target.value }))
            }
          />
          {errors.vestingDays && (
            <span className="text-red-600 text-xs mt-1">
              {errors.vestingDays}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">TGE unlock (%) *</span>
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.tgeUnlockPct}
            onChange={(e) =>
              setForm((p) => ({ ...p, tgeUnlockPct: e.target.value }))
            }
          />
          {errors.tgeUnlockPct && (
            <span className="text-red-600 text-xs mt-1">
              {errors.tgeUnlockPct}
            </span>
          )}
        </label>
      </div>
    </section>
  )
}

export default TermsSection
